import React, { useState, useEffect, useCallback } from 'react';

const CATEGORIES = ['business', 'personal'];
const STATUS_STYLES = {
  'new':         { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  'exploring':   { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  'in-progress': { color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
};

const s = {
  heading: { fontSize: '1.3rem', fontWeight: '700', color: '#e0e0e0', marginBottom: '1.25rem' },
  addForm: {
    background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: '10px',
    padding: '1.25rem', marginBottom: '1.5rem',
  },
  formRow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' },
  input: {
    flex: '1', minWidth: '200px', padding: '0.6rem 0.85rem',
    background: '#0f0f23', border: '1px solid #2a2a4a', borderRadius: '7px',
    color: '#e0e0e0', fontSize: '0.95rem', outline: 'none',
  },
  select: {
    padding: '0.6rem 0.75rem', background: '#0f0f23', border: '1px solid #2a2a4a',
    borderRadius: '7px', color: '#e0e0e0', fontSize: '0.9rem', cursor: 'pointer',
  },
  addBtn: {
    padding: '0.6rem 1.2rem', background: '#4f46e5', border: 'none',
    borderRadius: '7px', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem',
  },
  ideaList: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  ideaCard: {
    background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: '10px',
    padding: '0.9rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
  },
  ideaText: { flex: 1, color: '#e0e0e0', fontSize: '0.95rem' },
  statusBtn: (status) => ({
    padding: '0.22rem 0.65rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '700',
    color: STATUS_STYLES[status].color, background: STATUS_STYLES[status].bg,
    border: `1px solid ${STATUS_STYLES[status].color}33`,
    cursor: 'pointer', flexShrink: 0, textTransform: 'capitalize',
  }),
  catBadge: {
    padding: '0.2rem 0.55rem', borderRadius: '4px', fontSize: '0.72rem',
    background: 'rgba(79,70,229,0.15)', color: '#818cf8', flexShrink: 0,
  },
  iconBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: '#555', padding: '0.25rem', fontSize: '1rem', flexShrink: 0,
  },
  empty: { color: '#555', textAlign: 'center', padding: '2rem', fontSize: '0.95rem' },
  error: { color: '#f87171', fontSize: '0.85rem', marginTop: '0.5rem' },
  hint: { color: '#555', fontSize: '0.75rem', marginTop: '0.4rem' },
};

export default function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('personal');
  const [error, setError] = useState('');

  const fetchIdeas = useCallback(async () => {
    try {
      const res = await fetch('/api/ideas', { credentials: 'same-origin' });
      if (res.ok) setIdeas(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchIdeas(); }, [fetchIdeas]);

  async function addIdea(e) {
    e.preventDefault();
    setError('');
    if (!newText.trim()) return;
    const res = await fetch('/api/ideas', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: newText, category: newCategory }),
    });
    if (res.ok) {
      const idea = await res.json();
      setIdeas(prev => [idea, ...prev]);
      setNewText('');
    } else {
      const d = await res.json();
      setError(d.error || 'Failed to add idea');
    }
  }

  async function cycleStatus(idea) {
    const res = await fetch(`/api/ideas/${idea.id}/cycle-status`, {
      method: 'PUT', credentials: 'same-origin',
    });
    if (res.ok) {
      const updated = await res.json();
      setIdeas(prev => prev.map(i => i.id === idea.id ? updated : i));
    }
  }

  async function deleteIdea(id) {
    const res = await fetch(`/api/ideas/${id}`, {
      method: 'DELETE', credentials: 'same-origin',
    });
    if (res.ok) setIdeas(prev => prev.filter(i => i.id !== id));
  }

  return (
    <div>
      <div style={s.heading}>Ideas</div>
      <form style={s.addForm} onSubmit={addIdea}>
        <div style={s.formRow}>
          <input
            style={s.input} value={newText} placeholder="New idea..."
            onChange={e => setNewText(e.target.value)} required
          />
          <select style={s.select} value={newCategory} onChange={e => setNewCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <button style={s.addBtn} type="submit">Add Idea</button>
        </div>
        {error && <div style={s.error}>{error}</div>}
        <div style={s.hint}>Click the status badge to cycle: new → exploring → in-progress</div>
      </form>

      <div style={s.ideaList}>
        {ideas.length === 0 && <div style={s.empty}>No ideas yet. Add one above.</div>}
        {ideas.map(idea => (
          <div key={idea.id} style={s.ideaCard}>
            <span style={s.ideaText}>{idea.text}</span>
            <span style={s.catBadge}>{idea.category}</span>
            <button style={s.statusBtn(idea.status)} onClick={() => cycleStatus(idea)} title="Click to advance status">
              {idea.status}
            </button>
            <button style={{ ...s.iconBtn, color: '#ef4444' }} onClick={() => deleteIdea(idea.id)} title="Delete">🗑</button>
          </div>
        ))}
      </div>
    </div>
  );
}
